<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import * as api from "@/api/GepService";
import { apiCallWithRetry } from "@/api/api-call-with-retry";
import VButton from "@/components/VButton.vue";
import { useConfigStore } from "@/stores/store.config";
import { useFacilityBookingLauncher } from "@/composables/useFacilityBookingLauncher";
import { dismissRevBoosterModal } from "@/stores/util.services";
import { track } from "@/utils/gep-tracker";
import { isRoomFallbackEnabled } from "@/utils/roomFallbackFeature";
import { requestRoomVerification } from "@/utils/roomVerificationFlow";
import { isRoomInvalid } from "@/utils/roomVerificationState";

type RevBooster = {
  _id?: string;
  code?: string;
  image?: string;
  title?: string;
  description?: string;
  priceOrDiscount?: string;
  buttonName?: string;
  redirection?: string;
  redirectionPath?: string;
  timeConfiguration?: string;
};

type CatalogueEntry = {
  active?: boolean;
  ref?: string;
};

const router = useRouter();
const config = useConfigStore();
const user = config.useUserStore();
const { launchFacilityBooking } = useFacilityBookingLauncher();
const booster = computed<RevBooster>(
  () => (user.selectedRevBooster || {}) as RevBooster,
);

const roomNumber = () =>
  String(user.attributes?.user_room_number || user.roomNumber || "");

// Fire once when the promotion becomes visible. RevBooster is only mounted
// while the pop-up modal is open (BaseModal is `v-if`), so mount == shown.
// Watching `selectedRevBooster` over-fires: closing a TIME promotion calls
// checkIntervalDuration(), which selects the next available promotion before
// the modal closes, emitting view_promotion for a promotion never shown.
onMounted(() => {
  const item = booster.value;
  if (!item?._id) return;
  track("view_promotion", {
    vouch_product: "homepage",
    room_number: roomNumber(),
    promotion_id: item._id,
    promotion_code: item.code,
    promotion_title: item.title,
    promotion_type: "revbooster",
  });
});

const requiresGuestRoom = async (lang: string) => {
  if (user.homepageConfig?.secureRoomVerificationEnabled === false) return true;
  if (
    isRoomFallbackEnabled() &&
    isRoomInvalid(config.apikey, user.merchantId || "")
  ) {
    await requestRoomVerification({ lang });
    return false;
  }
  if (user.genericQRCode === true) return true;

  try {
    const customer = await apiCallWithRetry(api.getCustomerDetails, {
      lang,
      token: user.token || "",
    });
    const roomNumber = customer?.custom_attributes?.user_room_number;
    if (roomNumber && roomNumber !== "null") return true;
  } catch (error) {
    console.error(error);
    if (isRoomFallbackEnabled()) return false;
  }

  if (isRoomFallbackEnabled()) {
    await requestRoomVerification({ lang, invalid: true });
    return false;
  }
  localStorage.removeItem("VCquery");
  await router.push({ name: "page_error_scan", query: { lang } });
  return false;
};

const openCatalogue = async (
  item: RevBooster,
  configKey: "roomService" | "inHouse" | "inHouse2",
  lang: string,
) => {
  if (!(await requiresGuestRoom(lang))) return;

  const catalogue = (
    (user.homepageConfig?.homepage?.[configKey] || []) as CatalogueEntry[]
  ).find((entry) => entry.active !== false && entry.ref);
  if (!catalogue?.ref) return;

  await router.push({
    name: "page_catalogue",
    query: {
      to: catalogue.ref,
      lang,
      actionType:
        configKey === "roomService" ? "launch-catalogue" : "launch-inhouse",
      origin: window.location.href,
      version: "3",
      itemId: item.redirectionPath || "",
      popUpCatalogue: "true",
      homePopUpId: item._id || "",
    },
  });
};

const openExternalUrl = (path: unknown) => {
  if (typeof path !== "string" || !path.trim()) return;
  try {
    const url = new URL(path, window.location.origin);
    if (["http:", "https:"].includes(url.protocol)) {
      window.open(url.toString(), "_blank", "noopener");
    }
  } catch {
    // Ignore invalid merchant-configured links.
  }
};

const openBooster = async () => {
  const item = booster.value;
  const lang = user.currentSelectedLang || user.defaultLang;
  track("select_promotion", {
    vouch_product: "homepage",
    room_number: roomNumber(),
    promotion_id: item._id,
    promotion_code: item.code,
    promotion_title: item.title,
    promotion_type: "revbooster",
  });
  if (item.timeConfiguration === "TIME" && item._id) {
    user.setIntervalDuration(item._id);
    await user.checkIntervalDuration();
    dismissRevBoosterModal();
  }

  const catalogueConfigKeys: Record<
    string,
    "roomService" | "inHouse" | "inHouse2"
  > = {
    ROOM_SERVICE: "roomService",
    IN_HOUSE: "inHouse",
    IN_HOUSE2: "inHouse2",
  };
  const catalogueConfigKey = catalogueConfigKeys[item.redirection];
  if (catalogueConfigKey) {
    await openCatalogue(item, catalogueConfigKey, lang);
    return;
  }

  const routes: Record<string, string> = {
    FACILITY: "/page/facilities_detail",
    TTD: "/page/thingstodo_detail",
    PROMOTION: "/page/promotion",
  };
  const path = routes[item.redirection];
  if (path) {
    const query =
      item.redirection === "PROMOTION"
        ? { lang, scrollToCode: item.redirectionPath }
        : { lang, id: item.redirectionPath };
    router.push({ path, query });
    return;
  }
  if (item.redirection === "FACILITY_BOOKING") {
    const result = await launchFacilityBooking(
      item.redirectionPath || "",
      lang,
    );
    if (result === "untrusted") openExternalUrl(item.redirectionPath);
    return;
  }
  if (item.redirection === "EXTERNAL_URL") {
    openExternalUrl(item.redirectionPath);
    return;
  }
  openExternalUrl(item.redirectionPath);
};
</script>

<template>
  <article class="rev-booster">
    <div class="gtm-content" aria-hidden="true">
      <div
        id="room_number"
        :title="
          String(user.attributes?.user_room_number || user.roomNumber || '')
        "
      ></div>
      <div id="promotion_id" :title="booster._id || ''"></div>
      <div id="promotion_code" :title="booster.code || ''"></div>
    </div>
    <img v-if="booster.image" :src="booster.image" :alt="booster.title || ''" />
    <h2>{{ booster.title }}</h2>
    <p>{{ booster.description }}</p>
    <strong>{{ booster.priceOrDiscount }}</strong>
    <VButton :caption="booster.buttonName || 'View'" @click="openBooster" />
  </article>
</template>

<style scoped>
.rev-booster {
  display: grid;
  gap: 12px;
  text-align: center;
}
.rev-booster img {
  width: 100%;
  max-height: 260px;
  object-fit: cover;
}
.rev-booster h2,
.rev-booster p {
  margin: 0;
}
.rev-booster h2 {
  color: #303030;
  font-size: 16px;
  line-height: 1.3;
}
.rev-booster p {
  padding: 0 8px;
  color: #7e7e7e;
  font-size: 13px;
  line-height: 1.35;
}
.gtm-content {
  display: none;
}
</style>
